import { createContext, useContext, useEffect, useRef, useState, type PropsWithChildren } from "react";
import type { AuthChangeEvent, Session } from "@supabase/supabase-js";

import { isMeaningfulSessionChange } from "@/lib/authSessionChange";
import { authLog } from "@/lib/auth-log";
import { authSessionLoadingTimeoutMs } from "@/lib/authSessionLoadingTimeout";
import { DEV_BYPASS_BEARER, DEV_BYPASS_EMAIL, DEV_BYPASS_USER_ID, isDevAuthBypass, isDevPageCheck } from "@/lib/devAuthBypass";
import { setHubCachedAccessToken } from "@/lib/hubAuthAccessToken";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import {
  clearAuthParamsFromUrl,
  getOAuthErrorFromQuery,
  hasMagicLinkTokenHashInQuery,
  hasOauthReturnParams,
  isAwaitingSupabaseUrlSession,
  parseImplicitSessionTokensFromHash,
} from "@/lib/oauth-url";

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const terminalAuthEvents: ReadonlySet<AuthChangeEvent> = new Set([
  "INITIAL_SESSION",
  "SIGNED_IN",
  "SIGNED_OUT",
]);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  /** Latest Supabase session reference for `isMeaningfulSessionChange` (avoids churn on token refresh). */
  const authSessionSyncRef = useRef<Session | null>(null);

  useEffect(() => {
    setHubCachedAccessToken(session?.access_token ?? null);
  }, [session?.access_token]);

  useEffect(() => {
    if (isDevAuthBypass() || isDevPageCheck()) {
      // Local clickable-UI testing: synthesise an authenticated session so the app renders
      // without Supabase sign-in. The API accepts DEV_BYPASS_BEARER under INTEGRATION_TEST=1.
      authLog("auth:devBypass", { userId: DEV_BYPASS_USER_ID });
      const fakeSession = {
        access_token: DEV_BYPASS_BEARER,
        token_type: "bearer",
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        refresh_token: "dev-auth-bypass",
        user: {
          id: DEV_BYPASS_USER_ID,
          email: DEV_BYPASS_EMAIL,
          aud: "authenticated",
          role: "authenticated",
          app_metadata: {},
          user_metadata: {},
          created_at: new Date(0).toISOString(),
        },
      } as unknown as Session;
      setSession(fakeSession);
      authSessionSyncRef.current = fakeSession;
      setLoading(false);
      return;
    }
    if (!isSupabaseConfigured || !supabase) {
      authLog("auth:init:skip", { reason: "not configured" });
      setSession(null);
      authSessionSyncRef.current = null;
      setHubCachedAccessToken(null);
      setLoading(false);
      return;
    }
    const search = window.location.search;
    const hash = window.location.hash;
    const oauthHint = hasOauthReturnParams(search, hash);
    const awaitingUrlSession = isAwaitingSupabaseUrlSession(search, hash);
    const urlOAuthError = getOAuthErrorFromQuery(search);
    authLog("auth:init", {
      hasOauthUrlParams: oauthHint,
      awaitingUrlSession,
      oauthErrorInQuery: urlOAuthError?.error,
      path: window.location.pathname + search,
    });
    let mounted = true;
    const hashTokens = parseImplicitSessionTokensFromHash(hash);
    const loadingTimeoutMs = authSessionLoadingTimeoutMs(oauthHint || hashTokens != null);
    const loadingFallback = window.setTimeout(() => {
      if (mounted) {
        authLog("auth:loadingFallback", {
          reason: "no terminal auth event",
          timeoutMs: loadingTimeoutMs,
          hasOauthUrlParams: oauthHint,
        });
        setLoading(false);
      }
    }, loadingTimeoutMs);
    const clearFallback = (): void => {
      window.clearTimeout(loadingFallback);
    };

    if (hashTokens) {
      authLog("auth:setSessionFromHash:start");
      void supabase.auth.setSession(hashTokens).then(({ data, error }) => {
        if (!mounted) return;
        if (error) {
          authLog("auth:setSessionFromHash:error", { message: error.message });
          clearFallback();
          setLoading(false);
          return;
        }
        clearAuthParamsFromUrl();
        if (data.session) {
          authLog("auth:setSessionFromHash:ok", { userId: data.session.user.id });
          setHubCachedAccessToken(data.session.access_token);
          setSession(data.session);
          authSessionSyncRef.current = data.session;
        }
        clearFallback();
        setLoading(false);
      });
    }

    void supabase.auth.getSession().then(({ data, error }) => {
      if (!mounted) return;
      if (hashTokens) {
        return;
      }
      const s = window.location.search;
      const h = window.location.hash;
      const errFromQuery = getOAuthErrorFromQuery(s);
      if (errFromQuery) {
        authLog("auth:getSession:oauthErrorInQuery", { ...errFromQuery, getSessionError: error?.message });
        setSession(null);
        authSessionSyncRef.current = null;
        clearFallback();
        setLoading(false);
        return;
      }
      const oauth = hasOauthReturnParams(s, h);
      const waitingUrlSession = isAwaitingSupabaseUrlSession(s, h);
      authLog("auth:getSession:result", {
        hasSession: Boolean(data.session),
        userId: data.session?.user.id,
        getSessionError: error?.message,
        hasOauthUrlParams: oauth,
        awaitingUrlSession: waitingUrlSession,
      });
      setHubCachedAccessToken(data.session?.access_token ?? null);
      setSession(data.session);
      authSessionSyncRef.current = data.session;
      if (data.session) {
        clearFallback();
        setLoading(false);
        return;
      }
      if (!oauth) {
        clearFallback();
        setLoading(false);
        return;
      }
      if (waitingUrlSession) {
        authLog("auth:getSession:awaitUrlSession", { message: "auth params in URL, waiting for client" });
        return;
      }
      clearFallback();
      setLoading(false);
    });

    if (hasMagicLinkTokenHashInQuery(search)) {
      const params = new URLSearchParams(search);
      const tokenHash = params.get("token_hash")?.trim() ?? "";
      if (tokenHash !== "") {
        authLog("auth:verifyOtp:start", { type: params.get("type") ?? "magiclink" });
        void supabase.auth
          .verifyOtp({
            token_hash: tokenHash,
            type: "email",
          })
          .then(({ error }) => {
            if (!mounted) return;
            if (error) {
              authLog("auth:verifyOtp:error", { message: error.message });
              clearFallback();
              setLoading(false);
              return;
            }
            clearAuthParamsFromUrl();
          });
      }
    }

    const { data: authListener } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      authLog("auth:onAuthStateChange", {
        event,
        hasSession: Boolean(newSession),
        userId: newSession?.user.id,
        email: newSession?.user.email,
      });
      const previous = authSessionSyncRef.current;
      authSessionSyncRef.current = newSession;
      setHubCachedAccessToken(newSession?.access_token ?? null);
      if (isMeaningfulSessionChange(previous, newSession)) {
        setSession(newSession);
      }
      if (terminalAuthEvents.has(event)) {
        clearFallback();
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
      clearFallback();
      authListener.subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextValue = {
    session,
    loading,
    signOut: async () => {
      authLog("auth:signOut:manual");
      if (supabase) {
        await supabase.auth.signOut();
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider.");
  return ctx;
}
